import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  Calendar,
  Camera,
  Check,
  ClipboardCopy,
  Clock,
  CreditCard,
  Crown,
  ExternalLink,
  FileText,
  Gamepad2,
  Globe,
  Hash,
  ImageIcon,
  Link2,
  Loader2,
  Lock,
  Mail,
  Pencil,
  Plus,
  RefreshCw,
  Send,
  Shield,
  Swords,
  Trash2,
  UserMinus,
  UserPlus,
  Users,
  X,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';

import {
  Avatar,
  Badge,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  Tabs,
  Textarea,
} from '../../../../components/ui';
import { trpc } from '../../../../lib/trpc';
import { limitsToFeatureList, formatPrice } from '../../../../lib/plan-features';

export const Route = createFileRoute('/app/clubs/$slug/settings')({
  component: ClubSettingsPage,
  validateSearch: (search: Record<string, unknown>) => ({
    tab: (typeof search.tab === 'string' ? search.tab : 'general') as string,
  }),
});

// ---------------------------------------------------------------------------
// Privacy options (reused from CreateClubModal)
// ---------------------------------------------------------------------------

type Privacy = 'public' | 'invite_only' | 'private';

const privacyOptions: { value: Privacy; label: string; description: string; icon: typeof Globe }[] = [
  { value: 'public', label: 'Public', description: 'Anyone can find and join', icon: Globe },
  { value: 'invite_only', label: 'Invite Only', description: 'Visible in discovery, join with invite code', icon: Shield },
  { value: 'private', label: 'Private', description: 'Hidden from discovery, invite link required', icon: Lock },
];

const roleConfig: Record<string, { label: string; color: 'brand' | 'purple' | 'blue' | 'gray' }> = {
  owner: { label: 'Owner', color: 'brand' },
  admin: { label: 'Admin', color: 'purple' },
  moderator: { label: 'Mod', color: 'blue' },
  member: { label: 'Member', color: 'gray' },
};

const baseSettingsTabs = [
  { id: 'general', label: 'General', icon: <Shield className="size-4" /> },
  { id: 'games', label: 'Games', icon: <Gamepad2 className="size-4" /> },
  { id: 'members', label: 'Members', icon: <Users className="size-4" /> },
  { id: 'applications', label: 'Applications', icon: <FileText className="size-4" /> },
];

const billingTab = { id: 'billing', label: 'Billing', icon: <CreditCard className="size-4" /> };

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function ClubSettingsPage() {
  const { slug } = Route.useParams();
  const { tab: activeTab } = Route.useSearch();
  const navigate = useNavigate();

  const { data: club, isLoading } = trpc.club.get.useQuery({ slug });
  const { data: myClubs } = trpc.club.getMyClubs.useQuery();
  const membership = myClubs?.find((c) => c.slug === slug);
  const isAdmin = membership?.role === 'owner' || membership?.role === 'admin';
  const isOwner = membership?.role === 'owner';

  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto p-4 md:p-6 py-8">
        <div className="h-8 w-48 rounded-lg bg-surface-700/40 animate-pulse mb-8" />
        <div className="h-12 w-full rounded-lg bg-surface-800/50 animate-pulse mb-6" />
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-16 rounded-xl bg-surface-800/50 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!club || !isAdmin) {
    navigate({ to: '/app/clubs/$slug', params: { slug } });
    return null;
  }

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link to="/app/clubs/$slug" params={{ slug }}>
          <Button variant="ghost" size="sm" className="!px-2">
            <ArrowLeft className="size-4" />
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-display font-bold text-white">Club Settings</h1>
          <p className="text-sm text-surface-400">{club.name}</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        tabs={isOwner ? [...baseSettingsTabs, billingTab] : baseSettingsTabs}
        activeTab={activeTab}
        onChange={(tab) => navigate({ search: { tab }, replace: true })}
      />

      {/* Tab content */}
      {activeTab === 'general' && <GeneralTab club={club} isOwner={isOwner} slug={slug} />}
      {activeTab === 'games' && <GamesTab clubId={club.id} />}
      {activeTab === 'members' && <MembersTab clubId={club.id} isOwner={isOwner} myRole={membership?.role ?? 'member'} />}
      {activeTab === 'applications' && <ApplicationsTab clubId={club.id} />}
      {activeTab === 'billing' && isOwner && <BillingTab clubId={club.id} slug={slug} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// General Tab
// ---------------------------------------------------------------------------

type ClubData = {
  id: string;
  name: string;
  description: string | null;
  privacy: 'public' | 'private' | 'invite_only';
  discordUrl: string | null;
  twitterUrl: string | null;
  websiteUrl: string | null;
  avatarUrl: string | null;
  bannerUrl: string | null;
};

function GeneralTab({ club, isOwner, slug }: { club: ClubData; isOwner: boolean; slug: string }) {
  const utils = trpc.useUtils();
  const navigate = useNavigate();

  const [name, setName] = useState(club.name);
  const [description, setDescription] = useState(club.description ?? '');
  const [privacy, setPrivacy] = useState<Privacy>(club.privacy);
  const [discordUrl, setDiscordUrl] = useState(club.discordUrl ?? '');
  const [twitterUrl, setTwitterUrl] = useState(club.twitterUrl ?? '');
  const [websiteUrl, setWebsiteUrl] = useState(club.websiteUrl ?? '');
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const avatarInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [pendingAvatarFile, setPendingAvatarFile] = useState<File | null>(null);
  const [pendingBannerFile, setPendingBannerFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const uploadMutation = trpc.imageUpload.getUploadUrl.useMutation();

  const handleFileSelect = (
    e: React.ChangeEvent<HTMLInputElement>,
    setPreview: (url: string | null) => void,
    currentPreview: string | null,
    setPendingFile: (f: File | null) => void,
    maxSize: number,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Use PNG, JPEG, or WebP.');
      return;
    }
    if (file.size > maxSize) {
      toast.error(`File too large. Max ${maxSize / (1024 * 1024)} MB.`);
      return;
    }

    if (currentPreview) URL.revokeObjectURL(currentPreview);
    setPreview(URL.createObjectURL(file));
    setPendingFile(file);
  };

  const uploadFile = async (file: File, category: 'club-avatar' | 'club-banner'): Promise<string> => {
    const { uploadUrl, publicUrl } = await uploadMutation.mutateAsync({
      category,
      entityId: club.id,
      contentType: file.type,
      fileSize: file.size,
    });

    const res = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type, 'x-amz-acl': 'public-read' },
      body: file,
    });

    if (!res.ok) throw new Error('Upload to storage failed');
    return publicUrl;
  };

  const updateMutation = trpc.club.update.useMutation({
    onSuccess: () => {
      toast.success('Club updated');
      utils.club.get.invalidate({ slug });
      utils.club.getMyClubs.invalidate();
    },
    onError: (err) => toast.error(err.message || 'Failed to update club'),
  });

  const deleteMutation = trpc.club.delete.useMutation({
    onSuccess: () => {
      toast.success('Club deleted');
      utils.club.getMyClubs.invalidate();
      utils.club.list.invalidate();
      navigate({ to: '/app/clubs' });
    },
    onError: () => toast.error('Failed to delete club'),
  });

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updates: Record<string, unknown> = {
        clubId: club.id,
        name: name.trim(),
        description: description.trim() || undefined,
        privacy,
        discordUrl: discordUrl.trim() || undefined,
        twitterUrl: twitterUrl.trim() || undefined,
        websiteUrl: websiteUrl.trim() || undefined,
      };

      if (pendingAvatarFile) {
        updates.avatarUrl = await uploadFile(pendingAvatarFile, 'club-avatar');
      }
      if (pendingBannerFile) {
        updates.bannerUrl = await uploadFile(pendingBannerFile, 'club-banner');
      }

      await updateMutation.mutateAsync(updates as Parameters<typeof updateMutation.mutate>[0]);
      setPendingAvatarFile(null);
      setPendingBannerFile(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges =
    name.trim() !== club.name ||
    (description.trim() || '') !== (club.description ?? '') ||
    privacy !== club.privacy ||
    (discordUrl.trim() || '') !== (club.discordUrl ?? '') ||
    (twitterUrl.trim() || '') !== (club.twitterUrl ?? '') ||
    (websiteUrl.trim() || '') !== (club.websiteUrl ?? '') ||
    !!pendingAvatarFile ||
    !!pendingBannerFile;

  return (
    <div className="space-y-8">
      {/* Club Info */}
      <section className="space-y-4">
        <h3 className="text-sm font-bold uppercase tracking-widest text-surface-500">Club Info</h3>
        <div className="space-y-4 p-5 rounded-xl bg-surface-800/30 border border-surface-700/30">
          <Input
            label="Club Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={50}
          />
          <Textarea
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={500}
            rows={3}
            resize="none"
            placeholder="What's your club about?"
          />
        </div>
      </section>

      {/* Avatar & Banner */}
      <section className="space-y-4">
        <h3 className="text-sm font-bold uppercase tracking-widest text-surface-500">Branding</h3>
        <div className="p-5 rounded-xl bg-surface-800/30 border border-surface-700/30 space-y-5">
          {/* Banner */}
          <div>
            <label className="text-sm font-medium text-surface-300 mb-2 block">Banner</label>
            <button
              type="button"
              onClick={() => bannerInputRef.current?.click()}
              className="group relative w-full h-32 rounded-xl border-2 border-dashed border-surface-600 hover:border-brand-500/60 bg-surface-800/60 hover:bg-surface-800 transition-all overflow-hidden"
            >
              {bannerPreview || club.bannerUrl ? (
                <>
                  <img
                    src={bannerPreview ?? club.bannerUrl!}
                    alt="Club banner"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-surface-950/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Camera className="size-6 text-white" />
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-full gap-1">
                  <ImageIcon className="size-6 text-surface-500 group-hover:text-brand-400 transition-colors" />
                  <span className="text-xs text-surface-500">Upload banner</span>
                </div>
              )}
            </button>
            <input
              ref={bannerInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => handleFileSelect(e, setBannerPreview, bannerPreview, setPendingBannerFile, 5 * 1024 * 1024)}
              className="hidden"
            />
          </div>

          {/* Avatar */}
          <div>
            <label className="text-sm font-medium text-surface-300 mb-2 block">Avatar</label>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => avatarInputRef.current?.click()}
                className="group relative size-20 rounded-2xl border-2 border-dashed border-surface-600 hover:border-brand-500/60 bg-surface-800/60 hover:bg-surface-800 transition-all overflow-hidden shrink-0"
              >
                {avatarPreview || club.avatarUrl ? (
                  <>
                    <img
                      src={avatarPreview ?? club.avatarUrl!}
                      alt="Club avatar"
                      className="size-full object-cover"
                    />
                    <div className="absolute inset-0 bg-surface-950/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Camera className="size-5 text-white" />
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center size-full">
                    <Camera className="size-5 text-surface-500 group-hover:text-brand-400 transition-colors" />
                  </div>
                )}
              </button>
              <p className="text-xs text-surface-500">
                {avatarPreview || club.avatarUrl ? 'Click to change' : 'Upload a club icon'}
              </p>
            </div>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => handleFileSelect(e, setAvatarPreview, avatarPreview, setPendingAvatarFile, 2 * 1024 * 1024)}
              className="hidden"
            />
          </div>
        </div>
      </section>

      {/* Privacy */}
      <section className="space-y-4">
        <h3 className="text-sm font-bold uppercase tracking-widest text-surface-500">Privacy</h3>
        <div className="grid gap-2">
          {privacyOptions.map((opt) => {
            const Icon = opt.icon;
            const selected = privacy === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setPrivacy(opt.value)}
                className={`group flex items-center gap-3.5 px-4 py-3 rounded-xl border text-left transition-all duration-200 ${
                  selected
                    ? 'border-brand-500/40 bg-brand-500/5 shadow-[inset_0_1px_0_rgba(38,217,181,0.1)]'
                    : 'border-surface-700/40 bg-surface-800/30 hover:border-surface-600 hover:bg-surface-800/60'
                }`}
              >
                <div className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors ${
                  selected
                    ? 'bg-brand-500/15 text-brand-400'
                    : 'bg-surface-700/50 text-surface-500 group-hover:text-surface-400'
                }`}>
                  <Icon className="size-4" />
                </div>
                <div className="min-w-0">
                  <p className={`text-sm font-semibold transition-colors ${selected ? 'text-white' : 'text-surface-300'}`}>
                    {opt.label}
                  </p>
                  <p className="text-xs text-surface-500">{opt.description}</p>
                </div>
                <div className={`ml-auto w-4 h-4 rounded-full border-2 transition-all shrink-0 ${
                  selected
                    ? 'border-brand-500 bg-brand-500 shadow-[0_0_8px_rgba(38,217,181,0.4)]'
                    : 'border-surface-600'
                }`}>
                  {selected && (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="w-1.5 h-1.5 rounded-full bg-surface-900" />
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Social Links */}
      <section className="space-y-4">
        <h3 className="text-sm font-bold uppercase tracking-widest text-surface-500">Social Links</h3>
        <div className="space-y-4 p-5 rounded-xl bg-surface-800/30 border border-surface-700/30">
          <Input
            label="Discord URL"
            placeholder="https://discord.gg/..."
            value={discordUrl}
            onChange={(e) => setDiscordUrl(e.target.value)}
          />
          <Input
            label="Twitter URL"
            placeholder="https://twitter.com/..."
            value={twitterUrl}
            onChange={(e) => setTwitterUrl(e.target.value)}
          />
          <Input
            label="Website URL"
            placeholder="https://..."
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
          />
        </div>
      </section>

      {/* Invite Management */}
      <section className="space-y-4">
        <h3 className="text-sm font-bold uppercase tracking-widest text-surface-500">Invitations</h3>

        {/* Invite Links */}
        <InviteLinksSection clubId={club.id} />

        {/* Direct Invite */}
        <DirectInviteSection clubId={club.id} />
      </section>

      {/* Save */}
      <div className="flex justify-end">
        <Button
          variant="primary"
          disabled={!hasChanges || isSaving || !name.trim()}
          leftIcon={isSaving ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
          onClick={handleSave}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Danger Zone */}
      {isOwner && (
        <section className="space-y-4">
          <h3 className="text-sm font-bold uppercase tracking-widest text-red-400">Danger Zone</h3>
          <div className="p-5 rounded-xl border border-red-500/30 bg-red-500/5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-white">Delete Club</p>
                <p className="text-xs text-surface-400 mt-0.5">Permanently delete this club and all its data. This cannot be undone.</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="!border-red-500/40 !text-red-400 hover:!bg-red-500/10"
                leftIcon={<Trash2 className="size-3.5" />}
                onClick={() => setShowDeleteModal(true)}
              >
                Delete Club
              </Button>
            </div>
          </div>

          <DeleteClubModal
            open={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            clubName={club.name}
            isPending={deleteMutation.isPending}
            onConfirm={() => deleteMutation.mutate({ clubId: club.id })}
          />
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Delete Club Confirmation Modal
// ---------------------------------------------------------------------------

function DeleteClubModal({
  open,
  onClose,
  clubName,
  isPending,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  clubName: string;
  isPending: boolean;
  onConfirm: () => void;
}) {
  const [confirmText, setConfirmText] = useState('');
  const canDelete = confirmText === clubName;

  return (
    <Modal open={open} onClose={onClose} className="!max-w-sm">
      <div className="px-6 pt-6 pb-1 flex items-center gap-3">
        <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/20">
          <AlertTriangle className="size-5 text-red-400" />
        </div>
        <div>
          <h2 className="text-xl font-display font-bold text-white">Delete Club</h2>
          <p className="text-sm text-surface-400">This action is irreversible</p>
        </div>
      </div>
      <ModalBody className="space-y-4">
        <p className="text-sm text-surface-300">
          Type <span className="font-semibold text-white">{clubName}</span> to confirm deletion.
        </p>
        <Input
          placeholder="Type club name to confirm"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          autoFocus
        />
      </ModalBody>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose} disabled={isPending}>Cancel</Button>
        <Button
          variant="primary"
          className="!bg-red-500 hover:!bg-red-600"
          disabled={!canDelete || isPending}
          leftIcon={isPending ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
          onClick={onConfirm}
        >
          {isPending ? 'Deleting...' : 'Delete Club'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Games Tab
// ---------------------------------------------------------------------------

function GamesTab({ clubId }: { clubId: string }) {
  const utils = trpc.useUtils();
  const { data: games, isLoading } = trpc.clubGame.list.useQuery({ clubId });
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingGame, setEditingGame] = useState<string | null>(null);

  const deleteMutation = trpc.clubGame.delete.useMutation({
    onSuccess: () => {
      toast.success('Game removed');
      utils.clubGame.list.invalidate({ clubId });
    },
    onError: () => toast.error('Failed to remove game'),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold uppercase tracking-widest text-surface-500">Club Games</h3>
        <Button variant="outline" size="sm" leftIcon={<Plus className="size-3.5" />} onClick={() => setShowAddModal(true)}>
          Add Game
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => <div key={i} className="h-16 rounded-xl bg-surface-800/50 animate-pulse" />)}
        </div>
      ) : games && games.length > 0 ? (
        <div className="space-y-2">
          {games.map((game) => (
            <div key={game.id} className="flex items-center gap-3 p-4 rounded-xl bg-surface-800/30 border border-surface-700/30">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-brand-500/10 border border-brand-500/20">
                <Gamepad2 className="size-5 text-brand-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display font-bold text-white truncate">{game.name}</p>
                {game.teamSize && <p className="text-xs text-surface-400">{game.teamSize}v{game.teamSize}</p>}
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" className="!px-2" onClick={() => setEditingGame(game.id)}>
                  <Pencil className="size-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="!px-2 !text-red-400 hover:!bg-red-500/10"
                  disabled={deleteMutation.isPending}
                  onClick={() => deleteMutation.mutate({ clubGameId: game.id })}
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-12 rounded-xl bg-surface-800/30 border border-surface-700/30 text-center">
          <Gamepad2 className="size-8 text-surface-500 mb-3" />
          <p className="text-sm text-surface-400">No games configured yet</p>
          <Button variant="outline" size="sm" className="mt-4" leftIcon={<Plus className="size-3.5" />} onClick={() => setShowAddModal(true)}>
            Add First Game
          </Button>
        </div>
      )}

      <GameFormModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        clubId={clubId}
        mode="create"
      />

      {editingGame && (
        <GameFormModal
          open={true}
          onClose={() => setEditingGame(null)}
          clubId={clubId}
          mode="edit"
          clubGameId={editingGame}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Game Form Modal (create/edit)
// ---------------------------------------------------------------------------

function GameFormModal({
  open,
  onClose,
  clubId,
  mode,
  clubGameId,
}: {
  open: boolean;
  onClose: () => void;
  clubId: string;
  mode: 'create' | 'edit';
  clubGameId?: string;
}) {
  const utils = trpc.useUtils();
  const { data: existingGame } = trpc.clubGame.list.useQuery(
    { clubId },
    { enabled: mode === 'edit' && !!clubGameId },
  );
  const game = existingGame?.find((g) => g.id === clubGameId);

  const [name, setName] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [description, setDescription] = useState('');

  // Sync edit data
  useState(() => {
    if (game) {
      setName(game.name);
      setTeamSize(game.teamSize?.toString() ?? '');
      setDescription(game.description ?? '');
    }
  });

  const createMutation = trpc.clubGame.create.useMutation({
    onSuccess: () => {
      toast.success('Game added');
      utils.clubGame.list.invalidate({ clubId });
      onClose();
      setName('');
      setTeamSize('');
      setDescription('');
    },
    onError: (err) => toast.error(err.message || 'Failed to add game'),
  });

  const updateMutation = trpc.clubGame.update.useMutation({
    onSuccess: () => {
      toast.success('Game updated');
      utils.clubGame.list.invalidate({ clubId });
      onClose();
    },
    onError: (err) => toast.error(err.message || 'Failed to update game'),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) return;

    const ts = teamSize ? parseInt(teamSize, 10) : undefined;

    if (mode === 'edit' && clubGameId) {
      updateMutation.mutate({
        clubGameId,
        name: trimmedName,
        teamSize: ts,
        description: description.trim() || undefined,
      });
    } else {
      createMutation.mutate({
        clubId,
        name: trimmedName,
        teamSize: ts,
        description: description.trim() || undefined,
      });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal open={open} onClose={onClose} className="!max-w-sm">
      <form onSubmit={handleSubmit}>
        <div className="px-6 pt-6 pb-1 flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-brand-500/10 border border-brand-500/20">
            <Gamepad2 className="size-5 text-brand-400" />
          </div>
          <h2 className="text-xl font-display font-bold text-white">
            {mode === 'create' ? 'Add Game' : 'Edit Game'}
          </h2>
        </div>
        <ModalBody className="space-y-4">
          <Input
            label="Game Name"
            placeholder="e.g. Valorant"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            autoFocus
          />
          <Input
            label="Team Size"
            placeholder="e.g. 5"
            type="number"
            value={teamSize}
            onChange={(e) => setTeamSize(e.target.value)}
          />
          <Textarea
            label="Description"
            placeholder="Optional description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={500}
            rows={2}
            resize="none"
          />
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" type="button" onClick={onClose} disabled={isPending}>Cancel</Button>
          <Button
            variant="primary"
            type="submit"
            disabled={!name.trim() || isPending}
            leftIcon={isPending ? <Loader2 className="size-4 animate-spin" /> : undefined}
          >
            {isPending ? 'Saving...' : mode === 'create' ? 'Add Game' : 'Save'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Members Tab
// ---------------------------------------------------------------------------

function MembersTab({ clubId, isOwner, myRole }: { clubId: string; isOwner: boolean; myRole: string }) {
  const utils = trpc.useUtils();
  const { data, isLoading } = trpc.clubMember.list.useQuery({ clubId, limit: 50 });
  const members = data?.items ?? [];

  const [kickTarget, setKickTarget] = useState<{ id: string; username: string } | null>(null);
  const [transferTarget, setTransferTarget] = useState<{ id: string; username: string } | null>(null);

  const updateRoleMutation = trpc.clubMember.updateRole.useMutation({
    onSuccess: () => {
      toast.success('Role updated');
      utils.clubMember.list.invalidate({ clubId });
    },
    onError: (err) => toast.error(err.message || 'Failed to update role'),
  });

  const kickMutation = trpc.clubMember.kick.useMutation({
    onSuccess: () => {
      toast.success('Member kicked');
      utils.clubMember.list.invalidate({ clubId });
      utils.club.get.invalidate();
      setKickTarget(null);
    },
    onError: (err) => toast.error(err.message || 'Failed to kick member'),
  });

  const transferMutation = trpc.clubMember.transferOwnership.useMutation({
    onSuccess: () => {
      toast.success('Ownership transferred');
      utils.clubMember.list.invalidate({ clubId });
      utils.club.getMyClubs.invalidate();
      setTransferTarget(null);
    },
    onError: (err) => toast.error(err.message || 'Failed to transfer ownership'),
  });

  const canChangeRole = (targetRole: string) => {
    if (isOwner) return targetRole !== 'owner';
    if (myRole === 'admin') return targetRole === 'member' || targetRole === 'moderator';
    return false;
  };


  const roleOptions = isOwner
    ? ['admin', 'moderator', 'member']
    : ['moderator', 'member'];

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-bold uppercase tracking-widest text-surface-500">
        Members
        <span className="ml-2 text-surface-600 font-normal normal-case tracking-normal">{members.length}</span>
      </h3>

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-14 rounded-xl bg-surface-800/50 animate-pulse" />)}
        </div>
      ) : (
        <div className="space-y-1">
          {members.map((member) => {
            const role = roleConfig[member.role] ?? roleConfig.member;
            return (
              <div key={member.id} className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-surface-800/40 transition-colors">
                {/* Avatar */}
                <div className="relative shrink-0">
                  <Avatar
                    size="sm"
                    variant="rounded"
                    src={member.avatarUrl}
                    initials={member.username.charAt(0)}
                    alt={member.username}
                  />
                  {member.role === 'owner' && (
                    <span className="absolute -top-1 -right-1">
                      <Crown className="size-3 text-yellow-400" />
                    </span>
                  )}
                </div>

                {/* Name + role */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{member.username}</p>
                </div>
                <Badge variant="subtle" color={role.color} size="sm">{role.label}</Badge>

                {/* Actions */}
                {canChangeRole(member.role) && (
                  <div className="flex items-center gap-1">
                    {/* Role dropdown (simple select-like) */}
                    <select
                      value={member.role}
                      onChange={(e) => {
                        updateRoleMutation.mutate({
                          clubId,
                          userId: member.userId,
                          role: e.target.value,
                        });
                      }}
                      className="text-xs bg-surface-800 border border-surface-600 rounded-lg px-2 py-1.5 text-surface-300 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    >
                      {roleOptions.map((r) => (
                        <option key={r} value={r}>{roleConfig[r]?.label ?? r}</option>
                      ))}
                    </select>

                    <Button
                      variant="ghost"
                      size="sm"
                      className="!px-2 !text-red-400 hover:!bg-red-500/10"
                      onClick={() => setKickTarget({ id: member.userId, username: member.username })}
                    >
                      <UserMinus className="size-3.5" />
                    </Button>

                    {isOwner && member.role !== 'owner' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="!px-2 !text-yellow-400 hover:!bg-yellow-500/10"
                        title="Transfer ownership"
                        onClick={() => setTransferTarget({ id: member.userId, username: member.username })}
                      >
                        <Crown className="size-3.5" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Kick confirmation */}
      {kickTarget && (
        <ConfirmModal
          open={true}
          onClose={() => setKickTarget(null)}
          title="Kick Member"
          description={`Are you sure you want to kick ${kickTarget.username} from the club?`}
          confirmLabel="Kick"
          isPending={kickMutation.isPending}
          variant="danger"
          icon={<UserMinus className="size-5 text-red-400" />}
          onConfirm={() => kickMutation.mutate({ clubId, userId: kickTarget.id })}
        />
      )}

      {/* Transfer ownership confirmation */}
      {transferTarget && (
        <ConfirmModal
          open={true}
          onClose={() => setTransferTarget(null)}
          title="Transfer Ownership"
          description={`Transfer club ownership to ${transferTarget.username}? You will become an admin.`}
          confirmLabel="Transfer"
          isPending={transferMutation.isPending}
          variant="warning"
          icon={<Crown className="size-5 text-yellow-400" />}
          onConfirm={() => transferMutation.mutate({ clubId, newOwnerId: transferTarget.id })}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Generic Confirm Modal
// ---------------------------------------------------------------------------

function ConfirmModal({
  open,
  onClose,
  title,
  description,
  confirmLabel,
  isPending,
  variant,
  icon,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description: string;
  confirmLabel: string;
  isPending: boolean;
  variant: 'danger' | 'warning';
  icon: React.ReactNode;
  onConfirm: () => void;
}) {
  const colorClass = variant === 'danger' ? '!bg-red-500 hover:!bg-red-600' : '!bg-yellow-500 hover:!bg-yellow-600 !text-surface-900';
  const bgClass = variant === 'danger' ? 'bg-red-500/10 border-red-500/20' : 'bg-yellow-500/10 border-yellow-500/20';

  return (
    <Modal open={open} onClose={onClose} className="!max-w-sm">
      <div className="px-6 pt-6 pb-1 flex items-center gap-3">
        <div className={`flex items-center justify-center w-10 h-10 rounded-xl border ${bgClass}`}>
          {icon}
        </div>
        <div>
          <h2 className="text-xl font-display font-bold text-white">{title}</h2>
        </div>
      </div>
      <ModalBody>
        <p className="text-sm text-surface-300">{description}</p>
      </ModalBody>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose} disabled={isPending}>Cancel</Button>
        <Button
          variant="primary"
          className={colorClass}
          disabled={isPending}
          leftIcon={isPending ? <Loader2 className="size-4 animate-spin" /> : undefined}
          onClick={onConfirm}
        >
          {isPending ? 'Processing...' : confirmLabel}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Invite Links Section
// ---------------------------------------------------------------------------

function InviteLinksSection({ clubId }: { clubId: string }) {
  const utils = trpc.useUtils();
  const { data: links, isLoading } = trpc.clubInvite.listLinks.useQuery({ clubId });
  const [showCreate, setShowCreate] = useState(false);
  const [maxUses, setMaxUses] = useState('');
  const [expiresInDays, setExpiresInDays] = useState('');

  const createMutation = trpc.clubInvite.createLink.useMutation({
    onSuccess: () => {
      toast.success('Invite link created');
      utils.clubInvite.listLinks.invalidate({ clubId });
      setShowCreate(false);
      setMaxUses('');
      setExpiresInDays('');
    },
    onError: (err) => toast.error(err.message || 'Failed to create link'),
  });

  const revokeMutation = trpc.clubInvite.revokeLink.useMutation({
    onSuccess: () => {
      toast.success('Invite link revoked');
      utils.clubInvite.listLinks.invalidate({ clubId });
    },
    onError: () => toast.error('Failed to revoke link'),
  });

  const handleCreate = () => {
    const opts: { maxUses?: number; expiresAt?: string } = {};
    if (maxUses) opts.maxUses = parseInt(maxUses, 10);
    if (expiresInDays) {
      const d = new Date();
      d.setDate(d.getDate() + parseInt(expiresInDays, 10));
      opts.expiresAt = d.toISOString();
    }
    createMutation.mutate({ clubId, ...opts });
  };

  return (
    <div className="space-y-3 p-5 rounded-xl bg-surface-800/30 border border-surface-700/30">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-surface-400 uppercase tracking-wider">Invite Links</p>
        <Button
          variant="outline"
          size="sm"
          leftIcon={<Plus className="size-3.5" />}
          onClick={() => setShowCreate(!showCreate)}
        >
          {showCreate ? 'Cancel' : 'Create Link'}
        </Button>
      </div>

      {showCreate && (
        <div className="space-y-3 p-4 rounded-lg bg-surface-900/50 border border-surface-700/40">
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Max Uses (optional)"
              placeholder="Unlimited"
              type="number"
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
            />
            <Input
              label="Expires in (days)"
              placeholder="Never"
              type="number"
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(e.target.value)}
            />
          </div>
          <Button
            variant="primary"
            size="sm"
            disabled={createMutation.isPending}
            leftIcon={createMutation.isPending ? <Loader2 className="size-3.5 animate-spin" /> : <Link2 className="size-3.5" />}
            onClick={handleCreate}
          >
            {createMutation.isPending ? 'Creating...' : 'Create'}
          </Button>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2].map((i) => <div key={i} className="h-12 rounded-lg bg-surface-700/30 animate-pulse" />)}
        </div>
      ) : links && links.length > 0 ? (
        <div className="space-y-2">
          {links.map((link) => {
            const joinUrl = `${window.location.origin}/app/clubs/join/${link.code}`;
            const isExpired = link.expiresAt && new Date(link.expiresAt) < new Date();
            const isUsedUp = link.maxUses !== null && link.useCount >= link.maxUses;

            return (
              <div
                key={link.id}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${
                  isExpired || isUsedUp
                    ? 'bg-surface-900/30 border-surface-700/30 opacity-60'
                    : 'bg-surface-900/50 border-surface-700/40'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <code className="text-xs text-brand-400 font-mono truncate block">{link.code}</code>
                  <div className="flex items-center gap-3 mt-1 text-[10px] text-surface-500">
                    {link.maxUses !== null && (
                      <span className="flex items-center gap-1">
                        <Hash className="size-3" />
                        {link.useCount}/{link.maxUses} uses
                      </span>
                    )}
                    {link.expiresAt && (
                      <span className="flex items-center gap-1">
                        <Clock className="size-3" />
                        {isExpired ? 'Expired' : `Expires ${new Date(link.expiresAt).toLocaleDateString()}`}
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="!px-2"
                  onClick={() => {
                    navigator.clipboard.writeText(joinUrl);
                    toast.success('Link copied');
                  }}
                >
                  <ClipboardCopy className="size-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="!px-2 !text-red-400 hover:!bg-red-500/10"
                  disabled={revokeMutation.isPending}
                  onClick={() => revokeMutation.mutate({ clubId, linkId: link.id })}
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-surface-500 py-2">No invite links created yet</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Direct Invite Section
// ---------------------------------------------------------------------------

function DirectInviteSection({ clubId }: { clubId: string }) {
  const [userId, setUserId] = useState('');

  const sendMutation = trpc.clubInvite.send.useMutation({
    onSuccess: () => {
      toast.success('Invite sent!');
      setUserId('');
    },
    onError: (err) => {
      if (err.data?.code === 'CONFLICT') {
        toast.error('User already has a pending invite or is a member');
      } else if (err.data?.code === 'NOT_FOUND') {
        toast.error('User not found');
      } else {
        toast.error(err.message || 'Failed to send invite');
      }
    },
  });

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId.trim()) return;
    sendMutation.mutate({ clubId, inviteeId: userId.trim() });
  };

  return (
    <div className="space-y-3 p-5 rounded-xl bg-surface-800/30 border border-surface-700/30">
      <p className="text-xs font-medium text-surface-400 uppercase tracking-wider">Direct Invite</p>
      <form onSubmit={handleSend} className="flex gap-3">
        <Input
          placeholder="User ID"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          className="flex-1"
        />
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={!userId.trim() || sendMutation.isPending}
          leftIcon={sendMutation.isPending ? <Loader2 className="size-3.5 animate-spin" /> : <Send className="size-3.5" />}
        >
          Send
        </Button>
      </form>
      <p className="text-xs text-surface-500">
        Enter a user ID to send them a direct invite to the club.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Billing Tab
// ---------------------------------------------------------------------------

function BillingTab({ clubId, slug }: { clubId: string; slug: string }) {
  const utils = trpc.useUtils();
  const { data: subscription, isLoading: subLoading } = trpc.subscription.getClubSubscription.useQuery({ clubId });
  const { data: plans, isLoading: plansLoading } = trpc.subscription.getPlans.useQuery();
  const { data: invoices } = trpc.subscription.getInvoices.useQuery({ clubId });
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('yearly');

  const competitivePlan = plans?.find((p) => p.slug === 'competitive');

  const checkoutMutation = trpc.subscription.createCheckoutSession.useMutation({
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (err) => toast.error(err.message || 'Failed to create checkout session'),
  });

  const portalMutation = trpc.subscription.createPortalSession.useMutation({
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (err) => toast.error(err.message || 'Failed to open billing portal'),
  });

  const cancelMutation = trpc.subscription.cancelSubscription.useMutation({
    onSuccess: () => {
      toast.success('Subscription will cancel at period end');
      utils.subscription.getClubSubscription.invalidate({ clubId });
      setShowCancelModal(false);
    },
    onError: (err) => toast.error(err.message || 'Failed to cancel subscription'),
  });

  if (subLoading || plansLoading) {
    return (
      <div className="space-y-6">
        <div className="h-32 rounded-3xl bg-surface-800/50 animate-pulse" />
        <div className="h-80 rounded-3xl bg-surface-800/50 animate-pulse" />
      </div>
    );
  }

  const plan = subscription?.plan;
  const status = subscription?.status;
  const isOnCasual = !subscription || plan?.tier === 'casual';
  const isTrialing = status === 'trialing';
  const isActive = status === 'active';
  const isPastDue = status === 'past_due';
  const isExpired = status === 'expired';
  const hasTrialed = !!subscription?.trialStartedAt;

  const trialEndsAt = subscription?.trialEndsAt ? new Date(subscription.trialEndsAt) : null;
  const graceEndsAt = subscription?.graceEndsAt ? new Date(subscription.graceEndsAt) : null;
  const now = new Date();
  const trialDaysLeft = trialEndsAt ? Math.max(0, Math.ceil((trialEndsAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))) : 0;
  const graceDaysLeft = graceEndsAt ? Math.max(0, Math.ceil((graceEndsAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))) : 0;

  const periodEnd = subscription?.currentPeriodEnd ? new Date(subscription.currentPeriodEnd) : null;

  const statusBadge: Record<string, { label: string; color: 'brand' | 'green' | 'yellow' | 'red' | 'gray' }> = {
    active: { label: 'Active', color: 'green' },
    trialing: { label: 'Trial', color: 'brand' },
    past_due: { label: 'Past Due', color: 'red' },
    canceled: { label: 'Canceled', color: 'gray' },
    expired: { label: 'Grace Period', color: 'yellow' },
  };

  const currentBadge = status ? statusBadge[status] : null;

  return (
    <div className="space-y-8">
      {/* ── Status Banners ── */}
      {isTrialing && trialDaysLeft <= 3 && trialDaysLeft > 0 && (
        <div className="flex items-center gap-3 px-5 py-3.5 rounded-xl border border-yellow-500/30 bg-yellow-500/5">
          <AlertTriangle className="size-4 text-yellow-400 shrink-0" />
          <p className="text-sm text-yellow-300 flex-1">
            Your trial ends in <span className="font-display font-bold">{trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''}</span>. Upgrade to keep your features.
          </p>
        </div>
      )}

      {isExpired && (
        <div className="flex items-center gap-3 px-5 py-3.5 rounded-xl border border-yellow-500/30 bg-yellow-500/5">
          <AlertTriangle className="size-4 text-yellow-400 shrink-0" />
          <p className="text-sm text-yellow-300 flex-1">
            Grace period — <span className="font-display font-bold">{graceDaysLeft} day{graceDaysLeft !== 1 ? 's' : ''}</span> remaining before downgrade.
          </p>
        </div>
      )}

      {isPastDue && (
        <div className="flex items-center gap-3 px-5 py-3.5 rounded-xl border border-red-500/30 bg-red-500/5">
          <AlertTriangle className="size-4 text-red-400 shrink-0" />
          <p className="text-sm text-red-300 flex-1">
            Payment failed. Update your payment method to avoid losing access.
          </p>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 !border-red-500/40 !text-red-400 hover:!bg-red-500/10"
            onClick={() => portalMutation.mutate({ clubId, returnUrl: window.location.href })}
          >
            Update Payment
          </Button>
        </div>
      )}

      {/* ── Current Plan ── */}
      <section className="space-y-4">
        <h3 className="text-sm font-bold uppercase tracking-widest text-surface-500">Current Plan</h3>
        <div className={`relative overflow-hidden rounded-2xl border p-6 ${
          isOnCasual
            ? 'bg-surface-950 border-surface-600'
            : 'bg-surface-900/90 backdrop-blur-xl border-brand-500/50 shadow-[0_0_50px_-12px_rgba(38,217,181,0.25)]'
        }`}>
          {/* Subtle glow for paid plans */}
          {!isOnCasual && (
            <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-80 h-40 bg-brand-500/8 rounded-full blur-[80px] pointer-events-none" />
          )}

          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-xl border ${
                isOnCasual
                  ? 'bg-surface-500/20 border-surface-500/40'
                  : 'bg-brand-500/20 border-brand-500/20'
              }`}>
                {isOnCasual
                  ? <Users className="size-6 text-surface-300" />
                  : <Swords className="size-6 text-brand-400" />
                }
              </div>
              <div>
                <div className="flex items-center gap-2.5">
                  <h4 className="font-display text-2xl font-bold text-white uppercase">
                    {plan?.name ?? 'Casual'}
                  </h4>
                  {currentBadge && (
                    <Badge variant="subtle" color={currentBadge.color} size="sm">
                      {currentBadge.label}
                    </Badge>
                  )}
                  {subscription?.cancelAtPeriodEnd && (
                    <Badge variant="subtle" color="yellow" size="sm">Canceling</Badge>
                  )}
                </div>
                <p className="text-sm text-surface-400 mt-1">
                  {isOnCasual && 'Free forever — core features included'}
                  {isTrialing && trialEndsAt && `Trial ends ${trialEndsAt.toLocaleDateString()}`}
                  {isActive && !subscription?.cancelAtPeriodEnd && periodEnd && `Renews ${periodEnd.toLocaleDateString()}`}
                  {isActive && subscription?.cancelAtPeriodEnd && periodEnd && `Access until ${periodEnd.toLocaleDateString()}`}
                  {isPastDue && 'Payment required to continue'}
                  {isExpired && graceEndsAt && `Downgrades ${graceEndsAt.toLocaleDateString()}`}
                </p>
              </div>
            </div>

            {!isOnCasual && (
              <Button
                variant="outline"
                size="sm"
                leftIcon={portalMutation.isPending ? <Loader2 className="size-3.5 animate-spin" /> : <ExternalLink className="size-3.5" />}
                disabled={portalMutation.isPending}
                onClick={() => portalMutation.mutate({ clubId, returnUrl: window.location.href })}
              >
                Manage Billing
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* ── Upgrade / Trial ── */}
      {(isOnCasual || isExpired) && competitivePlan && (
        <section className="space-y-4">
          <h3 className="text-sm font-bold uppercase tracking-widest text-surface-500">
            {isOnCasual ? 'Upgrade to Competitive' : 'Upgrade'}
          </h3>
          <div className="relative group">
            <div className="absolute -inset-[1px] bg-gradient-to-b from-brand-400 to-transparent rounded-2xl opacity-30 blur-sm group-hover:opacity-60 transition duration-500" />

            <div className="relative overflow-hidden rounded-2xl bg-surface-900/90 backdrop-blur-xl border border-brand-500/50 p-8 shadow-[0_0_50px_-12px_rgba(38,217,181,0.25)]">
              <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-96 h-48 bg-brand-500/6 rounded-full blur-[80px] pointer-events-none" />

              <div className="relative">
                {/* Billing Cycle Toggle — matches pricing page pill */}
                <div className="flex justify-center mb-8">
                  <div className="relative inline-flex bg-surface-900/80 p-0.5 rounded-full border border-surface-700 backdrop-blur-md ring-1 ring-white/5">
                    <div
                      className={`absolute top-0.5 bottom-0.5 left-0.5 w-[110px] rounded-full bg-surface-700 shadow-inner border border-white/5 transition-all duration-300 ease-[cubic-bezier(0.25,0.1,0.25,1.0)] ${
                        billingCycle === 'yearly' ? 'translate-x-[110px]' : 'translate-x-0'
                      }`}
                    >
                      <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-transparent rounded-full" />
                    </div>

                    <button
                      type="button"
                      onClick={() => setBillingCycle('monthly')}
                      className={`relative w-[110px] py-2 text-xs font-bold uppercase tracking-wider transition-colors duration-300 z-10 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 cursor-pointer ${
                        billingCycle === 'monthly' ? 'text-white' : 'text-surface-400 hover:text-surface-200'
                      }`}
                    >
                      Monthly
                    </button>

                    <button
                      type="button"
                      onClick={() => setBillingCycle('yearly')}
                      className={`relative w-[110px] py-2 text-xs font-bold uppercase tracking-wider transition-colors duration-300 z-10 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 cursor-pointer flex items-center justify-center gap-1.5 ${
                        billingCycle === 'yearly' ? 'text-white' : 'text-surface-400 hover:text-surface-200'
                      }`}
                    >
                      Yearly
                      <span
                        className={`px-1 py-0.5 text-[9px] rounded font-black transition-all duration-300 ${
                          billingCycle === 'yearly'
                            ? 'bg-brand-500 text-surface-950 shadow-[0_0_10px_rgba(38,217,181,0.5)]'
                            : 'bg-brand-500/20 text-brand-400'
                        }`}
                      >
                        -25%
                      </span>
                    </button>
                  </div>
                </div>

                {/* Price */}
                <div className="text-center mb-2">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="font-display text-5xl font-bold text-white">
                      {formatPrice(billingCycle === 'monthly' ? competitivePlan.monthlyPrice : Math.round(competitivePlan.yearlyPrice / 12))}
                    </span>
                    <span className="text-surface-400">/ mo</span>
                  </div>
                  <p className="text-sm mt-2 text-surface-400">
                    {billingCycle === 'yearly' ? (
                      <span className="text-brand-400 font-bold">
                        Billed {formatPrice(competitivePlan.yearlyPrice)} yearly (Save {formatPrice(competitivePlan.monthlyPrice * 12 - competitivePlan.yearlyPrice)})
                      </span>
                    ) : (
                      'Billed monthly'
                    )}
                  </p>
                </div>

                <div className="h-px w-full bg-gradient-to-r from-transparent via-brand-500/50 to-transparent my-8" />

                {/* Feature list — derived from plan limits */}
                <ul className="grid grid-cols-2 gap-3 mb-8">
                  {limitsToFeatureList(competitivePlan.limits).map((feat) => (
                    <li key={feat} className="flex items-start gap-3 group/item">
                      <div className="flex-shrink-0 mt-0.5 p-0.5 rounded-full bg-brand-500/20 text-brand-400 group-hover/item:text-brand-300 transition-colors">
                        <Check className="size-3.5 stroke-[3]" />
                      </div>
                      <span className="text-sm text-white font-medium group-hover/item:text-surface-100 transition-colors">
                        {feat}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTAs */}
                <div className="space-y-3">
                  <Button
                    variant="primary"
                    className="w-full"
                    leftIcon={checkoutMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <Zap className="size-4" />}
                    disabled={checkoutMutation.isPending}
                    onClick={() =>
                      checkoutMutation.mutate({
                        clubId,
                        planSlug: 'competitive',
                        billingCycle,
                        successUrl: `${window.location.origin}/app/clubs/${slug}/settings?upgraded=true`,
                        cancelUrl: window.location.href,
                      })
                    }
                  >
                    {checkoutMutation.isPending ? 'Redirecting to Stripe...' : 'Upgrade to Competitive'}
                  </Button>

                  {/* Trial CTA — only if on casual and never trialed */}
                  {isOnCasual && !hasTrialed && (
                    <button
                      type="button"
                      className="w-full py-2.5 text-sm font-bold text-brand-400 hover:text-brand-300 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 rounded-lg"
                      disabled={checkoutMutation.isPending}
                      onClick={() =>
                        checkoutMutation.mutate({
                          clubId,
                          planSlug: 'competitive',
                          billingCycle,
                          withTrial: true,
                          successUrl: `${window.location.origin}/app/clubs/${slug}/settings?trial=true`,
                          cancelUrl: window.location.href,
                        })
                      }
                    >
                      Or start a {competitivePlan.trialDays}-day free trial
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── Invoice History ── */}
      {invoices && invoices.length > 0 && (
        <section className="space-y-4">
          <h3 className="text-sm font-bold uppercase tracking-widest text-surface-500">Invoice History</h3>
          <div className="rounded-xl border border-surface-700 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-surface-700 bg-surface-800/50">
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-surface-400">Date</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-surface-400">Amount</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-surface-400">Status</th>
                  <th className="text-right px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv, i) => (
                  <tr key={i} className="border-b border-surface-700/50 last:border-0">
                    <td className="px-4 py-3 text-surface-200">
                      {new Date(inv.date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-white font-medium font-display">
                      {formatPrice(inv.amount)}
                    </td>
                    <td className="px-4 py-3">
                      <Badge color={inv.status === 'paid' ? 'green' : inv.status === 'open' ? 'yellow' : 'gray'}>
                        {inv.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {inv.url && (
                        <a
                          href={inv.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs font-bold text-surface-400 hover:text-white transition-colors"
                        >
                          View <ExternalLink className="size-3" />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ── Cancel Subscription ── */}
      {(isActive || isTrialing) && !subscription?.cancelAtPeriodEnd && (
        <section className="space-y-4">
          <h3 className="text-sm font-bold uppercase tracking-widest text-red-400">Cancel Subscription</h3>
          <div className="p-5 rounded-xl border border-red-500/30 bg-red-500/5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-white">Cancel your plan</p>
                <p className="text-xs text-surface-400 mt-0.5">
                  {isTrialing
                    ? 'You\'ll keep access until your trial ends, then be downgraded to Casual.'
                    : 'You\'ll keep access until the end of your billing period.'}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="!border-red-500/40 !text-red-400 hover:!bg-red-500/10"
                onClick={() => setShowCancelModal(true)}
              >
                Cancel Plan
              </Button>
            </div>
          </div>

          <ConfirmModal
            open={showCancelModal}
            onClose={() => setShowCancelModal(false)}
            title="Cancel Subscription"
            description={
              isTrialing
                ? 'Are you sure? You\'ll keep Competitive features until your trial ends, then be downgraded to Casual.'
                : 'Are you sure? You\'ll keep access until the end of your current billing period, then be downgraded to Casual.'
            }
            confirmLabel="Cancel Plan"
            isPending={cancelMutation.isPending}
            variant="danger"
            icon={<CreditCard className="size-5 text-red-400" />}
            onConfirm={() => cancelMutation.mutate({ clubId })}
          />
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Applications Tab
// ---------------------------------------------------------------------------

function ApplicationsTab({ clubId }: { clubId: string }) {
  const utils = trpc.useUtils();
  const { data: applications, isLoading } = trpc.clubInvite.listPendingApplications.useQuery({ clubId });

  const reviewMutation = trpc.clubInvite.reviewApplication.useMutation({
    onSuccess: (_, variables) => {
      toast.success(variables.status === 'approved' ? 'Application approved' : 'Application rejected');
      utils.clubInvite.listPendingApplications.invalidate({ clubId });
      utils.clubMember.list.invalidate({ clubId });
    },
    onError: (err) => toast.error(err.message || 'Failed to review application'),
  });

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-bold uppercase tracking-widest text-surface-500">
        Pending Applications
        {applications && applications.length > 0 && (
          <span className="ml-2 text-surface-600 font-normal normal-case tracking-normal">
            {applications.length}
          </span>
        )}
      </h3>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => <div key={i} className="h-24 rounded-xl bg-surface-800/50 animate-pulse" />)}
        </div>
      ) : applications && applications.length > 0 ? (
        <div className="space-y-3">
          {applications.map((app) => (
            <div
              key={app.id}
              className="p-4 rounded-xl bg-surface-800/30 border border-surface-700/30 space-y-3"
            >
              <div className="flex items-center gap-3">
                <Avatar
                  size="sm"
                  variant="rounded"
                  src={app.avatarUrl}
                  initials={app.username.charAt(0)}
                  alt={app.username}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{app.username}</p>
                  <p className="text-xs text-surface-500">
                    Applied {new Date(app.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {app.answers && app.answers.length > 0 && (
                <div className="p-3 rounded-lg bg-surface-900/50 border border-surface-700/30">
                  <p className="text-xs text-surface-500 font-medium mb-1">Response</p>
                  {app.answers.map((answer: string, i: number) => (
                    <p key={i} className="text-sm text-surface-300">{answer}</p>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-2 justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  className="!text-red-400 hover:!bg-red-500/10"
                  disabled={reviewMutation.isPending}
                  leftIcon={<X className="size-3.5" />}
                  onClick={() => reviewMutation.mutate({
                    applicationId: app.id,
                    clubId,
                    status: 'rejected',
                  })}
                >
                  Reject
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={reviewMutation.isPending}
                  leftIcon={reviewMutation.isPending ? <Loader2 className="size-3.5 animate-spin" /> : <Check className="size-3.5" />}
                  onClick={() => reviewMutation.mutate({
                    applicationId: app.id,
                    clubId,
                    status: 'approved',
                  })}
                >
                  Approve
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-12 rounded-xl bg-surface-800/30 border border-surface-700/30 text-center">
          <FileText className="size-8 text-surface-500 mb-3" />
          <p className="text-sm text-surface-400">No pending applications</p>
          <p className="text-xs text-surface-500 mt-1">
            Applications from users will appear here for review
          </p>
        </div>
      )}
    </div>
  );
}
